import { useState, useEffect } from 'react'
import { useApi } from '@/lib/api'
import { useCodeValues } from '@/hooks/use-code-values'
import { useFormatters } from '@/hooks/use-formatters'
import { saveAs } from 'file-saver'
import { normalizeUTCDateOnlyToLocal } from '@/lib/utils'

// Timesheet data interface - Pure API model
export interface Timesheet {
  EntityId: number                  // Entity ID
  WorkerName: string               // Worker name
  FromDate: string | null          // ISO date string (nullable)
  ToDate: string | null            // ISO date string (nullable)
  EngagementBillableUnits: number  // Billable units
  EngagementNonBillableUnits: number // Non-billable units
  TimeSheetStatusId: number        // Timesheet status ID (needs code lookup)
}

// Type-safe field selection based on Timesheet model
type TimesheetField = keyof Timesheet

// Default fields to select for timesheet reports
const DEFAULT_TIMESHEET_FIELDS: TimesheetField[] = [
  'EntityId',
  'WorkerName',
  'FromDate',
  'ToDate',
  'EngagementBillableUnits',
  'EngagementNonBillableUnits',
  'TimeSheetStatusId'
]

// Reports API types based on the Swagger specification
export interface ReportSearchParams {
  filter?: string
  orderBy?: string
  top?: number
  skip?: number
  search?: string
  facets?: string[]
}

export interface ReportResponse<T> {
  value: T[]
  '@odata.count'?: number
  '@odata.nextLink'?: string
  facets?: Record<string, any>
}

const TIMESHEET_REPORT_TYPE_ID = 31
const MAX_RECORDS_PER_REQUEST = 1000

export interface UseTimesheetReportsOptions {
  engagementId?: number
  autoFetch?: boolean
  searchParams?: ReportSearchParams
  currentPage?: number
  pageSize?: number
}

export interface UseTimesheetReportsResult {
  timesheets: Timesheet[]
  loading: boolean
  error: string | null
  totalCount: number
  isExporting: boolean
  exportError: string | null
  uniqueWorkerNames: string[]
  workerNamesLoading: boolean
  refetch: () => Promise<void>
  exportToCSV: (searchParams?: ReportSearchParams) => Promise<void>
}

export function useTimesheetReports(options: UseTimesheetReportsOptions = {}): UseTimesheetReportsResult {
  const { engagementId, autoFetch = true, searchParams = {}, currentPage = 1, pageSize = 10 } = options
  const { reportsApi } = useApi()
  const { getCodeValueText } = useCodeValues()
  const { formatDecimal, formatDate } = useFormatters()
  
  const [timesheets, setTimesheets] = useState<Timesheet[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [uniqueWorkerNames, setUniqueWorkerNames] = useState<string[]>([])
  const [workerNamesLoading, setWorkerNamesLoading] = useState(false)

  const buildQueryParams = (params: ReportSearchParams): URLSearchParams => {
    const queryParams = new URLSearchParams()
    
    // Always include our type-safe field selection
    queryParams.append('$select', DEFAULT_TIMESHEET_FIELDS.join(','))
    
    if (params.filter) queryParams.append('$filter', params.filter)
    if (params.orderBy) queryParams.append('$orderby', params.orderBy)
    if (params.top) queryParams.append('$top', params.top.toString())
    if (params.skip) queryParams.append('$skip', params.skip.toString())
    if (params.search) queryParams.append('$search', params.search)
    if (params.facets?.length) {
      params.facets.forEach(facet => queryParams.append('facets', facet))
    }
    
    return queryParams
  }

  const buildFilterWithEngagement = (baseFilter: string = ''): string => {
    let filter = baseFilter
    if (engagementId) {
      const engagementFilter = `EngagementId eq ${engagementId}`
      filter = filter ? `${filter} and ${engagementFilter}` : engagementFilter
    }
    return filter
  }

  const makeReportApiCall = async (params: ReportSearchParams): Promise<ReportResponse<Timesheet>> => {
    if (!reportsApi) {
      throw new Error('Reports API not available')
    }

    const queryParams = buildQueryParams(params)
    const path = `Report/${TIMESHEET_REPORT_TYPE_ID}?${queryParams.toString()}`
    return await reportsApi.get<ReportResponse<Timesheet>>(path)
  }

  const fetchTimesheets = async (params: ReportSearchParams = searchParams): Promise<void> => {
    try {
      setLoading(true)
      setError(null)

      // Build filter with engagement
      const filter = buildFilterWithEngagement(params.filter)

      // Calculate skip based on pagination
      const skip = (currentPage - 1) * pageSize
      const finalParams = { ...params, filter, top: pageSize, skip }

      const response = await makeReportApiCall(finalParams)

      // Transform date strings from UTC format to local format
      const transformedTimesheets = (response.value || []).map(timesheet => ({
        ...timesheet,
        FromDate: normalizeUTCDateOnlyToLocal(timesheet.FromDate),
        ToDate: normalizeUTCDateOnlyToLocal(timesheet.ToDate)
      }))

      setTimesheets(transformedTimesheets)
      setTotalCount(response['@odata.count'] || response.value?.length || 0)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch timesheets'
      setError(errorMessage)
      console.error('Timesheet reports fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchAllTimesheets = async (params: ReportSearchParams = {}): Promise<Timesheet[]> => {
    const allTimesheets: Timesheet[] = []
    let skip = 0
    let hasMoreData = true

    // Build filter with engagement
    const filter = buildFilterWithEngagement(params.filter)
    const finalParams = { ...params, filter }

    while (hasMoreData) {
      const batchParams = { ...finalParams, top: MAX_RECORDS_PER_REQUEST, skip }
      const response = await makeReportApiCall(batchParams)
      const batchTimesheets = response.value || []
      
      // Transform date strings from UTC format to local format
      const transformedBatch = batchTimesheets.map(timesheet => ({
        ...timesheet,
        FromDate: normalizeUTCDateOnlyToLocal(timesheet.FromDate),
        ToDate: normalizeUTCDateOnlyToLocal(timesheet.ToDate)
      }))
      
      allTimesheets.push(...transformedBatch)
      
      // Check if we have more data
      hasMoreData = batchTimesheets.length === MAX_RECORDS_PER_REQUEST
      skip += MAX_RECORDS_PER_REQUEST
    }

    return allTimesheets
  }

  const fetchUniqueWorkerNames = async (): Promise<void> => {
    try {
      setWorkerNamesLoading(true)
      // Fetch all timesheets for this engagement (no additional filters) to extract unique worker names
      const allTimesheets = await fetchAllTimesheets({})
      const names = [...new Set(allTimesheets.map(t => t.WorkerName).filter(Boolean))].sort()
      setUniqueWorkerNames(names)
    } catch (err) {
      console.error('Failed to fetch unique worker names:', err)
    } finally {
      setWorkerNamesLoading(false)
    }
  }

  const refetch = async (): Promise<void> => {
    await fetchTimesheets(searchParams)
  }

  const transformTimesheetToCSVRow = (timesheet: Timesheet): string[] => {
    return [
      `"${timesheet.EntityId}"`,
      `"${timesheet.WorkerName}"`,
      `"${formatDate(timesheet.FromDate, '-')}"`,
      `"${formatDate(timesheet.ToDate, '-')}"`,
      `"${formatDecimal(timesheet.EngagementBillableUnits)}"`,
      `"${formatDecimal(timesheet.EngagementNonBillableUnits)}"`,
      `"${getCodeValueText(timesheet.TimeSheetStatusId, 'timesheet.CodeTimeSheetStatus')}"`,
    ]
  }

  const generateCSV = (timesheets: Timesheet[]): string => {
    const headers = [
      'ID',
      'Worker Name',
      'From Date',
      'To Date',
      'Billable Units',
      'Non-Billable Units',
      'Status'
    ]

    const csvRows = [
      headers.join(','), // Header row
      ...timesheets.map(timesheet => transformTimesheetToCSVRow(timesheet).join(','))
    ]

    return csvRows.join('\n')
  }

  const generateFileName = (): string => {
    const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD format
    const engagementPart = engagementId ? `engagement-${engagementId}-` : ''
    return `${engagementPart}timesheets-${today}.csv`
  }

  const exportToCSV = async (exportParams: ReportSearchParams = searchParams): Promise<void> => {
    try {
      setIsExporting(true)
      setExportError(null)

      const allTimesheets = await fetchAllTimesheets(exportParams)
      
      if (allTimesheets.length === 0) {
        throw new Error('No timesheets found to export')
      }

      // Generate CSV content
      const csvContent = generateCSV(allTimesheets)
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const fileName = generateFileName()
      
      saveAs(blob, fileName)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export timesheets'
      setExportError(errorMessage)
      console.error('Timesheet export error:', err)
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    if (autoFetch) {
      fetchTimesheets(searchParams)
    }
  }, [engagementId, autoFetch, currentPage, pageSize, searchParams])

  // Fetch unique worker names once on mount (keyed on engagementId)
  useEffect(() => {
    if (engagementId) {
      fetchUniqueWorkerNames()
    }
  }, [engagementId])

  return {
    timesheets,
    loading,
    error,
    totalCount,
    isExporting,
    exportError,
    uniqueWorkerNames,
    workerNamesLoading,
    refetch,
    exportToCSV
  }
}